import React, { useEffect } from 'react';
import { Article as IArticle } from 't2-graphql-types';
import cx from 'classnames';
import ArticleHeader from '../ArticleHeader';
import { ArticleEditButton } from '../ArticleHeader/ArticleHeader';
import { TArticleInteractionData } from './ArticleInteractions/types';
import { showNotification } from '../NotificationBanner/showNotification';
import ArticleHeaderInfo from './ArticleHeaderInfo';
import ArticleInspireContainer from '../ArticleInspire/ArticleInspireContainer';
import ArticleBodyRenderer from '../ArticleBodyRenderer/ArticleBodyRenderer';
import ArticleCoverImage from './ArticleCoverImage';
import { ArticleType, TCollectData } from './types';
import { ArticleInspirePosition } from '../ArticleInspire/constants';
import { ArticleParentInfoContainer } from '../ArticleParentInfo/ArticleParentInfoContainer';
import { ArticleTransactionsSection } from '../ArticleTransactionsSection/ArticleTransactionsSection';
import { ArticleCoverImageInputLoader } from '../ArticleCoverImageInput/ArticleCoverImageInputLoader';
import { ArticleEditorTitleInputLoader } from '../ArticleEditorTitleInput/ArticleEditorTitleInputLoader';
import { ArticleBodyRendererLoader } from '../ArticleBodyRenderer/ArticleBodyRendererLoader';
import { ArticleHeaderInfoLoader } from './ArticleHeaderInfoLoader';

type TArticleProps = Pick<
  Required<IArticle>,
  | 'id'
  | 'title'
  | 'status'
  | 'lastUpdateDate'
  | 'readTime'
  | 'author'
  | 'publishedAt'
  | 'body'
  | 'arweaveId'
  | 'coverImage'
  | 'parentArticle'
  | 'totalTimePointsAccumulated'
  | 'commentsCount'
  | 'slug'
> & {
  usersWhoReadArticle?: TArticleInteractionData[];
  collect: TCollectData;
  loading: boolean;
  shouldEnableEdit: boolean;
};

const Article: React.FC<TArticleProps> = ({
  id,
  title,
  status,
  publishedAt,
  readTime,
  author,
  usersWhoReadArticle = [],
  body,
  coverImage,
  arweaveId,
  parentArticle,
  totalTimePointsAccumulated,
  commentsCount,
  collect,
  slug,
  loading,
  shouldEnableEdit,
}) => {
  const draftArticleNotification = () =>
    showNotification({ title: 'You are in preview mode', type: 'success', position: 'top-center' });

  useEffect(() => {
    if (status === 'draft') {
      draftArticleNotification();
    }
  }, [status]);

  return (
    <>
      <div className="-mx-4 md:mx-0 mt-10 col-span-full md:col-start-2 md:col-span-10 lg:col-start-3 lg:col-span-8 xl:col-start-4 xl:col-span-6">
        {!loading && <ArticleCoverImage coverImage={coverImage} />}
        {loading && <ArticleCoverImageInputLoader />}
      </div>
      <div className="relative col-span-full md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
        {shouldEnableEdit && !loading && <ArticleEditButton id={id} />}
        {!loading && <ArticleHeader id={id} title={title} />}
        {loading && (
          <div className="w-full mt-20 flex justify-center">
            <ArticleEditorTitleInputLoader />
          </div>
        )}
      </div>
      <div className="col-span-full md:col-span-10 md:col-start-2 lg:col-span-8 lg:col-start-3">
        {!loading && (
          <ArticleHeaderInfo
            id={id}
            publishedAt={publishedAt}
            readTime={readTime}
            author={author}
            usersWhoReadArticle={usersWhoReadArticle}
            totalTimePointsAccumulated={totalTimePointsAccumulated}
            commentsCount={commentsCount}
            slug={slug}
          />
        )}
        {loading && <ArticleHeaderInfoLoader />}
      </div>
      {!loading && (parentArticle?.type === 'QUOTE' || parentArticle?.title || parentArticle?.slug) && (
        <div className="mt-12 col-span-full md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4">
          <ArticleParentInfoContainer parentArticle={parentArticle} />
        </div>
      )}
      <div className="article-container col-span-full md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4">
        <div className="article-body mb-[48px] md:mb-24 mt-12 lg:mb-28">
          {!loading && <ArticleBodyRenderer body={body} articleId={id} />}
          {loading && <ArticleBodyRendererLoader />}
        </div>
      </div>
      <div
        className={cx(
          'min-h-[44px] col-span-full md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4 ',
          'flex flex-col items-baseline lg:items-center justify-between gap-5 mb-[96px]',
        )}
      >
        <div className="flex items-center justify-between w-full">
          <ArticleInspireContainer
            position={ArticleInspirePosition.postFooter}
            articleId={id}
            articleType={ArticleType.Article}
          />
        </div>
        <div className="w-full">
          {(collect?.hasOnchainNftReference || arweaveId) && (
            <ArticleTransactionsSection nftReference={collect.nftReference!} arweaveId={arweaveId!} />
          )}
        </div>
      </div>
    </>
  );
};
export default Article;
